import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowRight, Users, Home as HomeIcon, TreePine, ShoppingBag, Scale, Crown, FileText } from 'lucide-react';
import LandRightsNotice from '../components/LandRightsNotice';

const Home = () => {
  useEffect(() => {
    document.title = 'Kāinga | He Whakaputanga nga Hapū o te Tairawhiti';
    return () => {
      document.title = 'He Whakaputanga nga Hapū o te Tairawhiti | Customary Rights Platform';
    };
  }, []);

  const features = [
    {
      icon: <Scale className="h-8 w-8 text-ocean-teal" />,
      title: "Tangata Whenua Rights",
      description: "Understanding your customary rights as tangata whenua and how to exercise them in modern Aotearoa.",
      link: "/tangata-whenua-rights"
    },
    {
      icon: <Crown className="h-8 w-8 text-ocean-teal" />,
      title: "Tino Rangatiratanga",
      description: "Exploring self-determination and sovereignty rights for Māori people and iwi.",
      link: "/tinorangatiratanga"
    },
    {
      icon: <FileText className="h-8 w-8 text-ocean-teal" />,
      title: "He Whakaputanga",
      description: "The Declaration of Independence and its significance for Māori constitutional rights.",
      link: "/he-whakaputanga"
    },
    {
      icon: <HomeIcon className="h-8 w-8 text-ocean-teal" />,
      title: "Whenua & Housing",
      description: "Pathways to return to ancestral lands and establish sustainable housing solutions.",
      link: "/whenau-housing"
    },
    {
      icon: <TreePine className="h-8 w-8 text-ocean-teal" />,
      title: "Whenua & Gardening",
      description: "Reconnecting with the land through traditional and modern gardening practices.",
      link: "/whenau-gardening"
    },
    {
      icon: <Users className="h-8 w-8 text-ocean-teal" />,
      title: "Whakapapa",
      description: "Discover your genealogy and strengthen connections to iwi and hapū.",
      link: "/whakapapa"
    }
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            He Whakaputanga nga Hapu o te Tairawhiti
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto opacity-90">
            A platform for discussion on customary rights for hapū in Te Tairawhiti and throughout Aotearoa, 
            based on He Whakaputanga - the Declaration of Independence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/tangata-whenua-rights" className="btn-primary">
              Explore Your Rights <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link to="/store" className="btn-secondary bg-white text-ocean-teal hover:bg-ocean-teal hover:text-white">
              Visit Our Store <ShoppingBag className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="section-header">Customary Rights Discussion</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fostering discussion and understanding of hapū customary rights based on He Whakaputanga, 
              serving hapū throughout Aotearoa with knowledge and connection.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Link key={index} to={feature.link} className="card group hover:scale-105 transition-transform duration-300">
                <div className="flex items-center mb-4">
                  {feature.icon}
                  <h3 className="text-xl font-semibold ml-3 text-gray-800 group-hover:text-ocean-teal transition-colors">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">{feature.description}</p>
                <div className="flex items-center text-ocean-teal font-medium">
                  Learn More <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">
            Kia Kaha - Uphold Your Customary Rights
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            A space for hapū to discuss and understand their customary rights as established in He Whakaputanga, 
            fostering unity and strength across all hapū in Aotearoa.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="cultural-border">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">Learn</h3>
              <p className="text-gray-600">Understand your rights and cultural heritage</p>
            </div>
            <div className="cultural-border">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">Connect</h3>
              <p className="text-gray-600">Build relationships with whānau and whenua</p>
            </div>
            <div className="cultural-border">
              <h3 className="text-xl font-semibold mb-2 text-gray-800">Act</h3>
              <p className="text-gray-600">Take steps toward reconnection and empowerment</p>
            </div>
          </div>
        </div>
      </section>

      {/* Land Rights Notice */}
      <LandRightsNotice />
    </div>
  );
};

export default Home;
